<template>
  <div class="settings-container">
    <!-- Cabeçalho da página -->
    <div class="page-header">
      <h1 class="page-title">
        <i class="fa-solid fa-cog"></i>
        Configurações do Sistema
      </h1>
      <p class="page-subtitle">Configure o nome do site, título, cores dos temas e muito mais</p>
    </div>

    <!-- Loading -->
    <div v-show="loading" class="loading-container">
      <div class="loading-spinner">
        <i class="fa-solid fa-spinner fa-spin"></i>
        <span>Carregando configurações...</span>
      </div>
    </div>

    <!-- Container principal das configurações -->
    <div v-show="!loading" class="settings-content">
      
      <!-- Seção de Informações do Site -->
          <div class="settings-section">
        <h3 class="section-title">
              <i class="fa-solid fa-info-circle"></i>
              Informações do Site
          </h3>
        
        <div class="settings-grid">
          <div class="setting-field">
            <label class="field-label">
                  <i class="fa-solid fa-globe"></i>
                  Nome do Site
                </label>
                <input 
                  type="text" 
              class="field-input"
                  v-model="settings.siteName"
              @blur="saveSettings"
                  placeholder="Digite o nome do site"
                  maxlength="256"
                />
              </div>

          <div class="setting-field">
            <label class="field-label">
                  <i class="fa-solid fa-heading"></i>
                  Título do Site
                </label>
                <input 
                  type="text" 
              class="field-input"
                  v-model="settings.siteTitle"
              @blur="saveSettings"
                  placeholder="Digite o título do site"
                  maxlength="256"
                />
              </div>

          <div class="setting-field">
            <label class="field-label">
                  <i class="fa-solid fa-font"></i>
                  Subtítulo (Opcional)
                </label>
                <input 
                  type="text" 
              class="field-input"
                  v-model="settings.siteSubtitle"
              @blur="saveSettings"
                  placeholder="Digite o subtítulo do site"
                  maxlength="512"
                />
              </div>

          <div class="setting-field">
            <label class="field-label">
                  <i class="fa-solid fa-image"></i>
              Logo Tema Claro (URL)
                </label>
                  <input 
                    type="url" 
              class="field-input"
                    v-model="settings.logoLight"
              @blur="saveSettings"
                    placeholder="https://exemplo.com/logo-light.png"
                    maxlength="512"
                  />
                  <div v-if="settings.logoLight" class="logo-preview">
                    <img :src="settings.logoLight" alt="Preview Logo Claro" class="logo-preview-image" @error="handleImageError($event, 'light')">
                    <span class="logo-preview-label">Preview Tema Claro</span>
                  </div>
              </div>

          <div class="setting-field">
            <label class="field-label">
                  <i class="fa-solid fa-image"></i>
              Logo Tema Escuro (URL)
                </label>
                  <input 
                    type="url" 
              class="field-input"
                    v-model="settings.logoDark"
              @blur="saveSettings"
                    placeholder="https://exemplo.com/logo-dark.png"
                    maxlength="512"
                  />
                  <div v-if="settings.logoDark" class="logo-preview dark-preview">
                    <img :src="settings.logoDark" alt="Preview Logo Escuro" class="logo-preview-image" @error="handleImageError($event, 'dark')">
                    <span class="logo-preview-label">Preview Tema Escuro</span>
                  </div>
              </div>

          <div class="setting-field">
            <label class="field-label">
                  <i class="fa-solid fa-image"></i>
              Favicon (URL)
                </label>
                  <input 
                    type="url" 
              class="field-input"
                    v-model="settings.faviconUrl"
              @blur="saveSettings"
                    placeholder="https://exemplo.com/favicon.ico"
                    maxlength="512"
                  />
              </div>

          <div class="setting-field">
            <label class="field-label">
                  <i class="fa-solid fa-mobile-alt"></i>
              Versão do App Android
                </label>
                  <input 
                    type="text" 
              class="field-input field-input-readonly"
                    v-model="settings.appVersion"
                    placeholder="—"
                    maxlength="50"
                    readonly
                    disabled
                  />
                  <small class="field-hint">
                    <i class="fa-solid fa-info-circle"></i>
                    Atualizada automaticamente ao publicar em
                    <router-link to="/app-versions" class="hint-link">Versões do App</router-link>
                  </small>
              </div>
            </div>
          </div>

      <!-- Seção de Resultados Automáticos -->
      <div class="settings-section">
        <h3 class="section-title">
          <i class="fa-solid fa-robot"></i>
          Resultados Automáticos
        </h3>
        
        <div class="settings-grid">
          <div class="setting-field">
            <label class="field-label">
              <i class="fa-solid fa-toggle-on"></i>
              Ativar Resultados Automáticos
            </label>
            <div class="toggle-container">
              <label class="toggle-switch">
                <input 
                  type="checkbox" 
                  v-model="settings.autoResultsEnabled"
                  @change="saveSettings"
                />
                <span class="toggle-slider"></span>
              </label>
              <span class="toggle-label">
                {{ settings.autoResultsEnabled ? 'Ativado' : 'Desativado' }}
              </span>
            </div>
            <small class="field-hint">
              <i class="fa-solid fa-info-circle"></i>
              Quando ativado, o sistema buscará resultados automaticamente da API externa.
            </small>
          </div>
        </div>
      </div>

      <!-- Seção de Cores Web - Tema Claro -->
      <div class="settings-section">
        <h3 class="section-title">
              <i class="fa-solid fa-sun"></i>
          Cores do Tema Claro (Web)
        </h3>
        
        <div class="colors-grid">
          <ColorField 
            label="Cor Primária" 
            icon="fa-palette"
                    v-model="settings.lightPrimaryColor"
            @update="saveSettings"
                    placeholder="#3b82f6"
          />
          <ColorField 
            label="Cor Secundária" 
            icon="fa-palette"
                    v-model="settings.lightSecondaryColor"
            @update="saveSettings"
                    placeholder="#f8fafc"
          />
          <ColorField 
            label="Cor de Destaque" 
            icon="fa-palette"
                    v-model="settings.lightAccentColor"
            @update="saveSettings"
                    placeholder="#10b981"
          />
          <ColorField 
            label="Fundo Primário" 
            icon="fa-palette"
                    v-model="settings.lightBackgroundPrimary"
            @update="saveSettings"
            placeholder="#ffffff"
          />
          <ColorField 
            label="Fundo Secundário" 
            icon="fa-palette"
                    v-model="settings.lightBackgroundSecondary"
            @update="saveSettings"
                    placeholder="#f1f5f9"
          />
          <ColorField 
            label="Texto Primário" 
            icon="fa-palette"
                    v-model="settings.lightTextPrimary"
            @update="saveSettings"
                    placeholder="#1e293b"
          />
          <ColorField 
            label="Texto Secundário" 
            icon="fa-palette"
                    v-model="settings.lightTextSecondary"
            @update="saveSettings"
                    placeholder="#475569"
          />
          <ColorField 
            label="Cor da Borda" 
            icon="fa-palette"
                    v-model="settings.lightBorderColor"
            @update="saveSettings"
                    placeholder="#cbd5e1"
                  />
            </div>
          </div>

      <!-- Seção de Cores Web - Tema Escuro -->
      <div class="settings-section">
        <h3 class="section-title">
              <i class="fa-solid fa-moon"></i>
          Cores do Tema Escuro (Web)
        </h3>
        
        <div class="colors-grid">
          <ColorField 
            label="Cor Primária" 
            icon="fa-palette"
                    v-model="settings.darkPrimaryColor"
            @update="saveSettings"
                    placeholder="#60a5fa"
          />
          <ColorField 
            label="Cor Secundária" 
            icon="fa-palette"
                    v-model="settings.darkSecondaryColor"
            @update="saveSettings"
                    placeholder="#1a1a1d"
          />
          <ColorField 
            label="Cor de Destaque" 
            icon="fa-palette"
                    v-model="settings.darkAccentColor"
            @update="saveSettings"
                    placeholder="#10b981"
          />
          <ColorField 
            label="Fundo Primário" 
            icon="fa-palette"
                    v-model="settings.darkBackgroundPrimary"
            @update="saveSettings"
                    placeholder="#0a0a0b"
          />
          <ColorField 
            label="Fundo Secundário" 
            icon="fa-palette"
                    v-model="settings.darkBackgroundSecondary"
            @update="saveSettings"
                    placeholder="#1a1a1d"
          />
          <ColorField 
            label="Texto Primário" 
            icon="fa-palette"
                    v-model="settings.darkTextPrimary"
            @update="saveSettings"
                    placeholder="#f4f4f5"
          />
          <ColorField 
            label="Texto Secundário" 
            icon="fa-palette"
            v-model="settings.darkTextSecondary"
            @update="saveSettings"
            placeholder="#e4e4e7"
          />
          <ColorField 
            label="Cor da Borda" 
            icon="fa-palette"
            v-model="settings.darkBorderColor"
            @update="saveSettings"
            placeholder="#52525b"
                  />
                </div>
              </div>

      <!-- Seção de Cores Android - Tema Claro -->
      <div class="settings-section">
        <h3 class="section-title">
          <i class="fa-solid fa-mobile-alt"></i>
          Cores do Tema Android - Claro
        </h3>
        
        <div class="colors-grid">
          <ColorField 
            label="Cor Primária" 
            icon="fa-palette"
            v-model="settings.androidLightPrimary"
            @update="saveSettings"
            placeholder="#00714B"
          />
          <ColorField 
            label="Cor Secundária" 
            icon="fa-palette"
            v-model="settings.androidLightSecondary"
            @update="saveSettings"
            placeholder="#FFDF1B"
          />
          <ColorField 
            label="Cor Terciária" 
            icon="fa-palette"
            v-model="settings.androidLightTertiary"
            @update="saveSettings"
            placeholder="#521FBA"
          />
          <ColorField 
            label="Fundo" 
            icon="fa-palette"
            v-model="settings.androidLightBackground"
            @update="saveSettings"
            placeholder="#FFFFFF"
          />
          <ColorField 
            label="Superfície" 
            icon="fa-palette"
            v-model="settings.androidLightSurface"
            @update="saveSettings"
            placeholder="#EDEDED"
          />
          <ColorField 
            label="Texto em Primária" 
            icon="fa-palette"
            v-model="settings.androidLightOnPrimary"
            @update="saveSettings"
            placeholder="#FFFFFF"
          />
          <ColorField 
            label="Texto em Secundária" 
            icon="fa-palette"
            v-model="settings.androidLightOnSecondary"
            @update="saveSettings"
            placeholder="#FFFFFF"
          />
          <ColorField 
            label="Texto em Terciária" 
            icon="fa-palette"
            v-model="settings.androidLightOnTertiary"
            @update="saveSettings"
            placeholder="#FFFFFF"
          />
          <ColorField 
            label="Texto em Fundo" 
            icon="fa-palette"
            v-model="settings.androidLightOnBackground"
            @update="saveSettings"
            placeholder="#000000"
          />
          <ColorField 
            label="Texto em Superfície" 
            icon="fa-palette"
            v-model="settings.androidLightOnSurface"
            @update="saveSettings"
            placeholder="#000000"
          />
          <ColorField 
            label="Botão Primário" 
            icon="fa-palette"
            v-model="settings.androidLightButtonPrimary"
            @update="saveSettings"
            placeholder="#00724C"
          />
          <ColorField 
            label="Botão Secundário" 
            icon="fa-palette"
            v-model="settings.androidLightButtonSecondary"
            @update="saveSettings"
            placeholder="#002D1E"
          />
        </div>
      </div>

      <!-- Seção de Cores Android - Tema Escuro -->
      <div class="settings-section">
        <h3 class="section-title">
          <i class="fa-solid fa-mobile-alt"></i>
          Cores do Tema Android - Escuro
        </h3>
        
        <div class="colors-grid">
          <ColorField 
            label="Cor Primária" 
            icon="fa-palette"
            v-model="settings.androidDarkPrimary"
            @update="saveSettings"
            placeholder="#00714B"
          />
          <ColorField 
            label="Cor Secundária" 
            icon="fa-palette"
            v-model="settings.androidDarkSecondary"
            @update="saveSettings"
            placeholder="#FFDF1B"
          />
          <ColorField 
            label="Cor Terciária" 
            icon="fa-palette"
            v-model="settings.androidDarkTertiary"
            @update="saveSettings"
            placeholder="#521FBA"
          />
          <ColorField 
            label="Fundo" 
            icon="fa-palette"
            v-model="settings.androidDarkBackground"
            @update="saveSettings"
            placeholder="#141616"
          />
          <ColorField 
            label="Superfície" 
            icon="fa-palette"
            v-model="settings.androidDarkSurface"
            @update="saveSettings"
            placeholder="#2B2D2D"
          />
          <ColorField 
            label="Texto em Primária" 
            icon="fa-palette"
            v-model="settings.androidDarkOnPrimary"
            @update="saveSettings"
            placeholder="#FFFFFF"
          />
          <ColorField 
            label="Texto em Secundária" 
            icon="fa-palette"
            v-model="settings.androidDarkOnSecondary"
            @update="saveSettings"
            placeholder="#FFFFFF"
          />
          <ColorField 
            label="Texto em Terciária" 
            icon="fa-palette"
            v-model="settings.androidDarkOnTertiary"
            @update="saveSettings"
            placeholder="#FFFFFF"
          />
          <ColorField 
            label="Texto em Fundo" 
            icon="fa-palette"
            v-model="settings.androidDarkOnBackground"
            @update="saveSettings"
            placeholder="#FFFFFF"
          />
          <ColorField 
            label="Texto em Superfície" 
            icon="fa-palette"
            v-model="settings.androidDarkOnSurface"
            @update="saveSettings"
            placeholder="#FFFFFF"
          />
          <ColorField 
            label="Botão Primário" 
            icon="fa-palette"
            v-model="settings.androidDarkButtonPrimary"
            @update="saveSettings"
            placeholder="#00724C"
          />
          <ColorField 
            label="Botão Secundário" 
            icon="fa-palette"
            v-model="settings.androidDarkButtonSecondary"
            @update="saveSettings"
            placeholder="#002D1E"
          />
        </div>
      </div>

            </div>

  </div>
</template>

<script>
import mainService from "@/services/mainService";
import { http, handleErrorMessage } from "@/services/config";
import { toast } from "@/composables/useToast";
import ColorField from "@/components/utils/ColorField.vue";

export default {
  name: "ViewSettings",
  components: {
    ColorField
  },
  data() {
    return {
      loading: true,
      saving: false,
      saveTimeout: null,
      settings: {
        id: null,
        siteName: "",
        siteTitle: "",
        siteSubtitle: "",
        logoLight: "",
        logoDark: "",
        faviconUrl: "",
        appVersion: "",
        // Cores tema claro
        lightPrimaryColor: "",
        lightSecondaryColor: "",
        lightAccentColor: "",
        lightBackgroundPrimary: "",
        lightBackgroundSecondary: "",
        lightTextPrimary: "",
        lightTextSecondary: "",
        lightBorderColor: "",
        // Cores tema escuro
        darkPrimaryColor: "",
        darkSecondaryColor: "",
        darkAccentColor: "",
        darkBackgroundPrimary: "",
        darkBackgroundSecondary: "",
        darkTextPrimary: "",
        darkTextSecondary: "",
        darkBorderColor: "",
        // Cores Android tema claro
        androidLightPrimary: "",
        androidLightSecondary: "",
        androidLightTertiary: "",
        androidLightBackground: "",
        androidLightSurface: "",
        androidLightOnPrimary: "",
        androidLightOnSecondary: "",
        androidLightOnTertiary: "",
        androidLightOnBackground: "",
        androidLightOnSurface: "",
        // Cores Android tema escuro
        androidDarkPrimary: "",
        androidDarkSecondary: "",
        androidDarkTertiary: "",
        androidDarkBackground: "",
        androidDarkSurface: "",
        androidDarkOnPrimary: "",
        androidDarkOnSecondary: "",
        androidDarkOnTertiary: "",
        androidDarkOnBackground: "",
        androidDarkOnSurface: "",
        // Cores dos botões Android tema claro
        androidLightButtonPrimary: "",
        androidLightButtonSecondary: "",
        // Cores dos botões Android tema escuro
        androidDarkButtonPrimary: "",
        androidDarkButtonSecondary: "",
        // Resultados automáticos
        autoResultsEnabled: false
      }
    };
  },
  async mounted() {
    this.loading = true;
    await this.loadSettings();
  },
  methods: {
    async loadSettings() {
      this.loading = true;
      try {
        const response = await mainService.getActiveSettings();
        if (response.data) {
          // Merge response data with default structure to ensure all fields exist
          this.settings = {
            ...this.settings,
            ...response.data,
            // Ensure logo fields are properly initialized
            logoLight: response.data.logoLight || "",
            logoDark: response.data.logoDark || response.data.logoUrl || ""
          };
          this.applyThemeColors(this.settings);
        }
        await this.syncAppVersionFromLatest();
      } catch (error) {
        // Erro será tratado silenciosamente, mantendo valores padrão
        console.error('Erro ao carregar configurações:', error);
      } finally {
        this.loading = false;
      }
    },

    async syncAppVersionFromLatest() {
      try {
        const { data } = await http.get('/api/v1/app/version/latest');
        if (data?.versionName) {
          this.settings.appVersion = data.versionName;
        }
      } catch {
        // Sem versão publicada ainda — mantém o valor das Settings
      }
    },

    async saveSettings() {
      // Debounce - só salva após 1 segundo sem mudanças
      if (this.saveTimeout) {
        clearTimeout(this.saveTimeout);
      }

      // Não salvar se não tiver ID (ainda não foi carregado)
      if (!this.settings.id) {
        return;
      }

      this.saveTimeout = setTimeout(async () => {
        await this.performSave();
      }, 1000);
    },

    async performSave() {
      if (this.saving) return;

      try {
        this.saving = true;
        
        let response;
        
        if (this.settings.id) {
          // Atualizar configuração existente
          response = await mainService.updateSettings(this.settings);
        } else {
          // Criar nova configuração
          response = await mainService.insertSettings(this.settings);
        }

        if (response.data) {
          this.settings = { ...response.data };
          this.applyThemeColors(response.data);
          
          toast.success("Configurações salvas com sucesso!");
        }
              } catch (error) {
        const errorMessage = handleErrorMessage(error);
        toast.error("Erro ao salvar configurações: " + errorMessage);
      } finally {
        this.saving = false;
      }
    },

    applyThemeColors(settings) {
      const root = document.documentElement;
      const isDarkTheme = document.documentElement.hasAttribute('data-theme');
      
      // Aplicar título e favicon
      if (settings.siteTitle) {
        document.title = settings.siteTitle;
      }
      
      if (settings.faviconUrl) {
        this.updateFavicon(settings.faviconUrl);
      }

      // Aplicar cores do tema atual
      if (!isDarkTheme) {
        if (settings.lightPrimaryColor) {
          root.style.setProperty('--primary-color', settings.lightPrimaryColor);
        }
        if (settings.lightBackgroundPrimary) {
          root.style.setProperty('--bg-primary', settings.lightBackgroundPrimary);
        }
        if (settings.lightBackgroundSecondary) {
          root.style.setProperty('--bg-secondary', settings.lightBackgroundSecondary);
        }
        if (settings.lightTextPrimary) {
          root.style.setProperty('--text-primary', settings.lightTextPrimary);
        }
      } else {
        if (settings.darkPrimaryColor) {
          root.style.setProperty('--primary-color', settings.darkPrimaryColor);
        }
        if (settings.darkBackgroundPrimary) {
          root.style.setProperty('--bg-primary', settings.darkBackgroundPrimary);
        }
        if (settings.darkBackgroundSecondary) {
          root.style.setProperty('--bg-secondary', settings.darkBackgroundSecondary);
        }
        if (settings.darkTextPrimary) {
          root.style.setProperty('--text-primary', settings.darkTextPrimary);
        }
      }
    },

    updateFavicon(faviconUrl) {
      const existingFavicon = document.querySelector('link[rel="icon"]') || 
                            document.querySelector('link[rel="shortcut icon"]');
      if (existingFavicon) {
        existingFavicon.remove();
      }

      const newFavicon = document.createElement('link');
      newFavicon.rel = 'icon';
      newFavicon.type = 'image/x-icon';
      newFavicon.href = faviconUrl;
      document.head.appendChild(newFavicon);
    },

    handleImageError(event, theme) {
      event.target.style.display = 'none';
      const container = event.target.parentElement;
      if (container) {
        const errorMsg = container.querySelector('.image-error') || document.createElement('div');
        errorMsg.className = 'image-error';
        errorMsg.textContent = `Erro ao carregar logo do tema ${theme === 'light' ? 'claro' : 'escuro'}`;
        if (!container.querySelector('.image-error')) {
          container.appendChild(errorMsg);
        }
      }
    }
  }
};
</script>

<style scoped>
/* Container principal */
.settings-container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 2rem;
  min-height: 100vh;
}

/* Cabeçalho */
.page-header {
  margin-bottom: 2rem;
  text-align: center;
}

.page-title {
  color: var(--text-primary, #1e293b);
  font-size: 2rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
}

.page-title i {
  color: var(--accent-color, #3b82f6);
  font-size: 1.75rem;
}

.page-subtitle {
  color: var(--text-secondary, #64748b);
  font-size: 1.1rem;
  margin: 0;
}

/* Loading */
.loading-container {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 4rem;
}

.loading-spinner {
  display: flex;
  align-items: center;
  gap: 1rem;
  color: var(--accent-color, #3b82f6);
  font-size: 1.1rem;
}

.loading-spinner i {
  font-size: 1.5rem;
}

/* Conteúdo das configurações */
.settings-content {
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

/* Seções */
.settings-section {
  background: var(--card-background, #ffffff);
  border-radius: 16px;
  padding: 2rem;
  border: 1px solid var(--border-color, #e2e8f0);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.section-title {
  color: var(--text-primary, #1e293b);
  font-size: 1.25rem;
  font-weight: 600;
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding-bottom: 0.75rem;
  border-bottom: 2px solid var(--accent-color, #3b82f6);
}

.section-title i {
  color: var(--accent-color, #3b82f6);
  font-size: 1.1rem;
}

/* Grid de configurações */
.settings-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1.5rem;
}

.colors-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1.5rem;
}

/* Campos */
.setting-field {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.field-label {
  color: var(--text-secondary, #64748b);
  font-weight: 500;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.field-label i {
  color: var(--accent-color, #3b82f6);
  font-size: 0.85rem;
}

.field-input {
  padding: 0.75rem 1rem;
  border: 2px solid var(--border-color, #e2e8f0);
  border-radius: 8px;
  font-size: 0.95rem;
  transition: all 0.2s ease;
  background: var(--input-background, #ffffff);
  color: var(--text-primary, #1e293b);
}

.field-input:focus {
  outline: none;
  border-color: var(--accent-color, #3b82f6);
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.field-input:invalid {
  border-color: var(--danger-color, #ef4444);
}

.field-input-readonly,
.field-input:disabled {
  opacity: 0.9;
  cursor: not-allowed;
  background: var(--bg-tertiary, #f1f5f9);
}

.hint-link {
  color: var(--accent-color, #3b82f6);
  font-weight: 600;
  text-decoration: none;
}

.hint-link:hover {
  text-decoration: underline;
}

/* Field Hint */
.field-hint {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--text-secondary, #64748b);
  font-size: 0.8rem;
  margin-top: 0.25rem;
  padding: 0.5rem;
  background: rgba(59, 130, 246, 0.05);
  border-radius: 6px;
  border-left: 3px solid var(--accent-color, #3b82f6);
}

.field-hint i {
  color: var(--accent-color, #3b82f6);
  font-size: 0.75rem;
}

[data-theme="dark"] .field-hint {
  background: rgba(59, 130, 246, 0.1);
}

/* Logo Preview */
.logo-preview {
  margin-top: 0.75rem;
  padding: 1rem;
  background: #f8fafc;
  border: 2px dashed #cbd5e1;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.3s ease;
}

.logo-preview.dark-preview {
  background: #1a1a1d;
  border-color: #52525b;
}

[data-theme="dark"] .logo-preview {
  background: #252529;
  border-color: #52525b;
}

[data-theme="dark"] .logo-preview.dark-preview {
  background: #0a0a0b;
  border-color: #71717a;
}

.logo-preview-image {
  max-width: 150px;
  max-height: 50px;
  object-fit: contain;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.logo-preview-label {
  font-size: 0.75rem;
  color: var(--text-secondary, #64748b);
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.dark-preview .logo-preview-label {
  color: #9ca3af;
}

.image-error {
  color: var(--danger-color, #ef4444);
  font-size: 0.75rem;
  text-align: center;
  padding: 0.5rem;
  background: rgba(239, 68, 68, 0.1);
  border-radius: 4px;
  margin-top: 0.5rem;
}

/* Toggle Switch Styles */
.toggle-container {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-top: 0.5rem;
}

.toggle-switch {
  position: relative;
  display: inline-block;
  width: 60px;
  height: 34px;
}

.toggle-switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.toggle-slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #ccc;
  transition: 0.4s;
  border-radius: 34px;
}

.toggle-slider:before {
  position: absolute;
  content: "";
  height: 26px;
  width: 26px;
  left: 4px;
  bottom: 4px;
  background-color: white;
  transition: 0.4s;
  border-radius: 50%;
}

.toggle-switch input:checked + .toggle-slider {
  background-color: var(--primary-color, var(--accent-color, #3b82f6));
}

.toggle-switch input:checked + .toggle-slider:before {
  transform: translateX(26px);
}

.toggle-switch input:focus + .toggle-slider {
  box-shadow: 0 0 1px var(--primary-color, var(--accent-color, #3b82f6));
}

.toggle-label {
  font-weight: 600;
  color: var(--text-primary);
  font-size: 0.9rem;
}

/* Responsividade */
@media (max-width: 768px) {
  .settings-container {
    padding: 1rem;
  }
  
  .settings-grid {
    grid-template-columns: 1fr;
  }
  
  .colors-grid {
    grid-template-columns: 1fr;
  }
  
  .page-title {
    font-size: 1.5rem;
  }
}

@media (max-width: 480px) {
  .settings-container {
    padding: 0.5rem;
  }
  
  .settings-section {
    padding: 1rem;
  }
  
  .page-title {
    font-size: 1.25rem;
    flex-direction: column;
    gap: 0.5rem;
  }
}
</style> 